import React, { useState } from "react";
import { GripVertical, Pencil, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/context/ToastContext";
import { useDragReorder } from "@/lib/useDragReorder";
import { cn } from "@/lib/utils";
import type { NavGroup } from "./nav";
import type { PinnedNav } from "./usePinnedNav";

/**
 * Pencil button: what is on the sidebar and in which order. Drag a module by its grip (or press the arrow keys on it) to move it,
 * × takes it off, and the modules below are added with +. Everything else stays under More.
 */
export function CustomizeNav({ groups, pins }: { groups: NavGroup[]; pins: PinnedNav }) {
  const { error } = useToast();
  const [open, setOpen] = useState(false);
  const full = pins.count >= pins.max;
  const reorder = useDragReorder(pins.pinned.length, pins.move);
  const others = groups.map((g) => ({ ...g, items: g.items.filter((i) => !pins.isPinned(i.key)) })).filter((g) => g.items.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Customise the sidebar"
              className="flex size-11 shrink-0 items-center justify-center rounded-xl text-sidebar-foreground outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              <Pencil className="size-[18px]" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        {!open && (
          <TooltipContent side="right" sideOffset={8}>
            Customise the sidebar
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent side="right" align="end" sideOffset={12} collisionPadding={12} className="w-[300px] border-sidebar-border bg-sidebar p-0 text-sidebar-foreground shadow-2xl">
        <div className="border-b border-sidebar-border px-4 py-3">
          <div className="text-[15px] font-semibold text-white">Customise the sidebar</div>
          <p className="mt-0.5 text-[12px] text-sidebar-foreground/80">
            Drag to reorder. {pins.count} of {pins.max} on the sidebar; the rest are under More.
          </p>
        </div>

        <div className="max-h-[min(420px,calc(100vh-190px))] overflow-y-auto px-2 py-2">
          <div className="px-2 pb-1 pt-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-sidebar-foreground/60">On the sidebar</div>
          {pins.pinned.map(({ item }, i) => {
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                {...reorder.rowProps(i)}
                className={cn(
                  "flex h-9 items-center gap-1.5 rounded-lg border-t-2 border-transparent pl-1 pr-1.5 text-[13px] transition-colors hover:bg-sidebar-accent",
                  reorder.dragging === i && "opacity-40",
                  reorder.over === i && reorder.dragging !== null && reorder.dragging !== i && "border-sidebar-ring",
                )}
              >
                <button
                  type="button"
                  aria-label={`Move ${item.label}. Drag, or use the up and down arrow keys`}
                  title="Drag to reorder"
                  {...reorder.handleProps(i)}
                  className="flex size-6 shrink-0 cursor-grab items-center justify-center rounded text-sidebar-foreground/60 outline-none hover:text-white focus-visible:ring-1 focus-visible:ring-sidebar-ring active:cursor-grabbing"
                >
                  <GripVertical className="size-4" />
                </button>
                <Icon className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                <button
                  type="button"
                  onClick={() => pins.toggle(item.key)}
                  disabled={pins.pinned.length <= 1}
                  aria-label={`Take ${item.label} off the sidebar`}
                  className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-sidebar-foreground/60 outline-none hover:bg-white/10 hover:text-white focus-visible:ring-1 focus-visible:ring-sidebar-ring disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            );
          })}

          {others.length > 0 && (
            <div className="mt-2 border-t border-sidebar-border pt-1">
              <div className="px-2 pb-0.5 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-sidebar-foreground/60">Add to the sidebar</div>
              {others.map((g) => (
                <div key={g.key}>
                  <div className="px-2 pb-0.5 pt-1.5 text-[11px] text-sidebar-foreground/50">{g.title}</div>
                  {g.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => !pins.toggle(item.key) && error("The sidebar is full", `Up to ${pins.max} modules can be on it. Take one off first.`)}
                        disabled={full}
                        aria-label={`Add ${item.label} to the sidebar`}
                        className="flex h-8 w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 text-left text-[13px] outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-1 focus-visible:ring-sidebar-ring disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
                      >
                        <Icon className="size-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        <Plus className="size-3.5 shrink-0 opacity-60" />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-sidebar-border px-4 py-2.5 text-[12px]">
          <button type="button" onClick={pins.reset} className="text-sidebar-ring underline-offset-2 hover:underline">
            Reset to default
          </button>
          <button type="button" onClick={() => setOpen(false)} className="rounded-md bg-sidebar-primary px-3 py-1.5 font-semibold text-sidebar-primary-foreground hover:opacity-90">
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
